import { useMedia } from '../ui/responsive';
import {
  hexA,
  pathFromPoints,
  connectorGeometry,
  rectOf,
  type BoardDoc,
  type Point,
  type Rect,
} from '@scribbleplot/shared';
import { color, font } from '../tokens';
import type { ResizeDir } from './gestures';

/**
 * Everything drawn over the items: strokes, connectors, selection affordances,
 * comment pins, and peer cursors.
 *
 * All of it lives in the same scaled world container as the items, so overlays
 * stay glued to content when the camera moves. Sizes that must stay constant on
 * screen are divided by the scale.
 */

/** Freehand strokes and connectors share one SVG layer above the items. */
export function VectorLayer({
  doc,
  strokes,
  sel,
  accent,
  scale,
  liveStroke,
  connectFrom,
  connLive,
  onStrokeDown,
  onStrokeContext,
}: {
  /** Full document — connector endpoints may lie outside the rendered window. */
  doc: BoardDoc;
  /** Strokes actually worth drawing, already culled to the viewport. */
  strokes: BoardDoc['strokes'];
  sel: readonly string[];
  accent: string;
  scale: number;
  liveStroke: Point[] | null;
  connectFrom: string | null;
  connLive: Point | null;
  onStrokeDown: (id: string, event: React.PointerEvent) => void;
  onStrokeContext: (id: string, event: React.MouseEvent) => void;
}) {
  const anchor = connectFrom ? rectOf(connectFrom, doc.items) : null;

  return (
    <svg
      width={10}
      height={10}
      aria-hidden
      style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none', zIndex: 99_990 }}
    >
      {strokes.map((stroke) => {
        const selected = sel.includes(stroke.id);
        return (
          <path
            key={stroke.id}
            d={pathFromPoints(stroke.pts)}
            onPointerDown={(event) => onStrokeDown(stroke.id, event)}
            onContextMenu={(event) => onStrokeContext(stroke.id, event)}
            style={{
              fill: 'none',
              stroke: stroke.color,
              strokeWidth: stroke.w + (selected ? 0.6 : 0),
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              // Only the inked line is clickable, not its bounding box.
              pointerEvents: 'stroke',
              cursor: 'default',
              filter: selected ? `drop-shadow(0 0 5px ${hexA(accent, 0.9)})` : 'none',
            }}
          />
        );
      })}

      {liveStroke && liveStroke.length > 1 && (
        <path
          d={pathFromPoints(liveStroke)}
          style={{ fill: 'none', stroke: accent, strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' }}
        />
      )}

      {doc.conns.map((conn) => {
        const a = rectOf(conn.a, doc.items);
        const b = rectOf(conn.b, doc.items);
        if (!a || !b) return null;
        const { path, arrow } = connectorGeometry(a, b);
        return (
          <g key={conn.id}>
            <path d={path} style={{ fill: 'none', stroke: hexA(accent, 0.8), strokeWidth: 2 }} />
            <path d={arrow} style={{ fill: hexA(accent, 0.9), stroke: 'none' }} />
          </g>
        );
      })}

      {anchor && connLive && (
        <path
          d={`M${anchor.x + anchor.w / 2} ${anchor.y + anchor.h / 2} L${connLive[0]} ${connLive[1]}`}
          style={{ fill: 'none', stroke: accent, strokeWidth: 2, strokeDasharray: '7 6' }}
        />
      )}
    </svg>
  );
}

/** Rubber-band selection rectangle. */
export function Marquee({ rect, accent, scale }: { rect: Rect; accent: string; scale: number }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        border: `${(1 / scale).toFixed(2)}px solid ${accent}`,
        background: hexA(accent, 0.08),
        zIndex: 99_997,
        pointerEvents: 'none',
      }}
    />
  );
}

/** Preview of the area being drawn with the frame tool. */
export function FrameDraft({ rect, accent }: { rect: Rect; accent: string }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        border: `1.5px dashed ${accent}`,
        borderRadius: 14,
        background: hexA(accent, 0.05),
        zIndex: 99_997,
        pointerEvents: 'none',
      }}
    />
  );
}

const DIRS: { dir: ResizeDir; cursor: string }[] = [
  { dir: 'nw', cursor: 'nwse-resize' },
  { dir: 'ne', cursor: 'nesw-resize' },
  { dir: 'sw', cursor: 'nesw-resize' },
  { dir: 'se', cursor: 'nwse-resize' },
];

/** Corner resize handles for a single selected item. */
export function ResizeHandles({
  rect,
  accent,
  scale,
  onGrab,
}: {
  rect: Rect;
  accent: string;
  scale: number;
  onGrab: (dir: ResizeDir, event: React.PointerEvent) => void;
}) {
  const coarse = useMedia('(pointer: coarse)');
  // Touch handles keep a comfortable screen-space target at every zoom.
  const size = (coarse ? 26 : 10) / scale;
  const at = (dir: ResizeDir) => ({
    x: dir.includes('w') ? rect.x : rect.x + rect.w,
    y: dir.includes('n') ? rect.y : rect.y + rect.h,
  });

  return (
    <>
      {DIRS.map(({ dir, cursor }) => {
        const { x, y } = at(dir);
        return (
          <div
            key={dir}
            data-resize={dir}
            onPointerDown={(event) => onGrab(dir, event)}
            style={{
              position: 'absolute',
              left: x - size / 2,
              top: y - size / 2,
              width: size,
              height: size,
              background: color.bg,
              border: `${(1.6 / scale).toFixed(2)}px solid ${accent}`,
              borderRadius: 2.5 / scale,
              cursor,
              zIndex: 99_998,
            }}
          />
        );
      })}
    </>
  );
}

/** Live cursors for other people on the board. */
export function PeerCursors({
  peers,
  scale,
}: {
  peers: readonly { id: string; name: string; color: string; cursor: { x: number; y: number } | null }[];
  scale: number;
}) {
  return (
    <>
      {peers.map((peer) =>
        peer.cursor ? (
          <div
            key={peer.id}
            aria-hidden
            style={{
              position: 'absolute',
              left: peer.cursor.x,
              top: peer.cursor.y,
              zIndex: 99_994,
              pointerEvents: 'none',
              // Counter-scale so the pointer stays a constant size on screen.
              transform: `scale(${(1 / scale).toFixed(3)})`,
              transformOrigin: 'top left',
              transition: 'left .11s linear, top .11s linear',
            }}
          >
            <svg width={15} height={17}>
              <path
                d="M1.5 1.5 L13 10 L7.5 10.6 L10 15.5 L7.7 16.4 L5.4 11.6 L1.5 14.5 Z"
                style={{ fill: peer.color, stroke: color.bg, strokeWidth: 1 }}
              />
            </svg>
            <div
              style={{
                marginLeft: 11,
                marginTop: -3,
                background: peer.color,
                color: color.bg,
                fontSize: 10.5,
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: 7,
                whiteSpace: 'nowrap',
                display: 'inline-block',
              }}
            >
              {peer.name}
            </div>
          </div>
        ) : null,
      )}
    </>
  );
}

/** Placeholder shown when a board has nothing on it yet. */
export function EmptyHint({ canEdit }: { canEdit: boolean }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      <div style={{ fontFamily: font.mono, fontSize: 13, letterSpacing: '.06em', color: color.textGhost }}>
        {canEdit ? 'double-click anywhere to start' : 'this board is empty'}
      </div>
    </div>
  );
}

/** Full-canvas target shown while files are dragged over the board. */
export function DropZone({ accent }: { accent: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 10,
        border: `2px dashed ${accent}`,
        borderRadius: 16,
        background: hexA(accent, 0.06),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: accent,
        zIndex: 70,
        pointerEvents: 'none',
      }}
    >
      <div style={{ fontFamily: font.mono, fontSize: 12, letterSpacing: '.06em' }}>drop images anywhere</div>
    </div>
  );
}
